import { makeObservable, observable, action, runInAction } from 'mobx';
import {
  RecipeGroupState,
  RecipeColumnState,
  RecipeRowState,
  RecipeState,
  CommentMode,
  RowType,
  Source,
  Visibility,
} from '../types';
import { evaluateColumnVisibility } from '../visibilityUtils';
import { RowStore } from './RowStore';
import type { RecipeStore } from './RecipeStore';
import type { FormulaScope } from './FormulaScope';
import { log } from '@/lib/utils/logger';

/**
 * MobX store for a recipe group (table).
 *
 * Replaces Knockout's RecipeGroupVM:
 * - Observable array of rows (like ko.observableArray)
 * - Column definitions (static)
 * - Row management (add/remove)
 *
 * Groups formulas by context for FormulaScope.apply(group, row).
 */
export class GroupStore {
  id: number | undefined;
  symbol: string;
  name: string;
  color: string;
  rowType: RowType;
  scrolling: boolean;
  allowSeveral: boolean;
  copyLastRow: boolean;
  comments: CommentMode;
  regexFirstInclude?: string;
  regexFirstExclude?: string;
  filterColumn?: string;
  canAddGroup: boolean;
  canRemoveGroup: boolean;
  commentColSpan: number;
  showAllComments: boolean;
  columns: RecipeColumnState[] = [];
  rows: RowStore[] = [];
  /** See {@link RecipeGroupState.calculatedVisibility}; mirrored from loaded state if ever set. */
  calculatedVisibility?: Set<string>;

  private formulaScope: FormulaScope;
  private recipe: RecipeStore; // Reference to parent recipe for cross-group cache clearing

  constructor(data: RecipeGroupState, recipe: RecipeStore, formulaScope: FormulaScope) {
    this.recipe = recipe; // Store reference for cross-group cache clearing
    this.id = data.id;
    this.symbol = data.symbol;
    this.name = data.name;
    this.color = data.color;
    this.rowType = data.rowType;
    this.scrolling = data.scrolling;
    this.allowSeveral = data.allowSeveral;
    this.copyLastRow = data.copyLastRow;
    this.comments = data.comments;
    this.regexFirstInclude = data.regexFirstInclude;
    this.regexFirstExclude = data.regexFirstExclude;
    this.filterColumn = data.filterColumn;
    this.canAddGroup = data.canAddGroup;
    this.canRemoveGroup = data.canRemoveGroup;
    this.commentColSpan = data.commentColSpan;
    this.showAllComments = data.showAllComments;
    this.columns = data.columns;
    this.calculatedVisibility = data.calculatedVisibility;

    this.formulaScope = formulaScope;

    // Create row stores
    this.rows = data.rows.map((rowData) => new RowStore(rowData, this, formulaScope));

    makeObservable(this, {
      rows: observable,
      columns: observable,
      calculatedVisibility: observable,
      addRow: action,
      removeRow: action,
      clearGroup: action,
      updateRow: action,
    });
  }

  /**
   * Get column definition by symbol
   */
  getColumn(symbol: string): RecipeColumnState | undefined {
    return this.columns.find((col) => col.symbol === symbol);
  }

  /**
   * Full recipe as plain state (for visibility formulas that reference other groups).
   * Call once per render when evaluating many columns to avoid repeated RecipeStore.toState work.
   */
  getRecipeStateSnapshot(): RecipeState {
    return this.recipe.toState();
  }

  /**
   * Whether a column with {@link Visibility.IfCondition} should appear in this group's table.
   * The table uses one header row for all rows, so the column is shown if any row satisfies the
   * condition (formula, refColumn, or cross-group ref), matching how {@link RecipeGroup} renders.
   *
   * @param readonly Same meaning as recipe UI readonly mode (affects IfModifiable-style rules inside helpers).
   * @param recipeState Optional snapshot from getRecipeStateSnapshot; when omitted, a new snapshot is built.
   */
  isIfConditionColumnVisible(
    column: RecipeColumnState,
    readonly: boolean,
    recipeState: RecipeState = this.recipe.toState()
  ): boolean {
    if (column.visibility !== Visibility.IfCondition) {
      return false;
    }
    return this.rows.some((row) =>
      evaluateColumnVisibility(column, recipeState, this.formulaScope, row, readonly, this)
    );
  }

  /**
   * Get row by symbol
   */
  getRow(symbol: string): RowStore | undefined {
    return this.rows.find((row) => row.symbol === symbol);
  }

  /**
   * Add a new empty row
   */
  addRow(_sourceRowIndex?: number): void {
    const maxId = this.rows.reduce((max, row) => {
      const rowId = row.id ?? 0;
      return Math.max(max, rowId > 0 ? rowId : 0);
    }, 0);
    const newId = maxId + 1;
    const newRowData: RecipeRowState = {
      id: newId,
      symbol: `row_${newId}`,
      name: '',
      comment: '',
      definedInTemplate: false,
      values: new Map(),
      showComment: false,
      scrollTo: false,
    };

    const newRow = new RowStore(newRowData, this, this.formulaScope);
    this.rows.push(newRow);

    // Clear formula caches and trigger MobX reactivity for all rows
    // This ensures formulas recalculate immediately after adding a row
    // Use runInAction to separate cacheVersion increment from the row addition
    // This prevents triggering the save reaction unnecessarily
    this.clearAllCaches();
    runInAction(() => {
      this.recipe.groups.forEach((group) => {
        group.rows.forEach((row) => {
          // clearAllCaches already calls clearFormulaCache which clears the cache
          // But we need to increment cacheVersion to trigger MobX reactivity
          row.cacheVersion++;
        });
      });
    });

    log.debug(`[GroupStore] Added new row to group ${this.symbol}`);
  }

  /**
   * Remove a row
   */
  removeRow(rowIndex: number): void {
    if (rowIndex < 0 || rowIndex >= this.rows.length) {
      log.warn(`[GroupStore] Invalid row index ${rowIndex}`);
      return;
    }

    this.rows.splice(rowIndex, 1);

    // Clear formula caches and trigger MobX reactivity for all rows
    // This ensures formulas recalculate immediately after removing a row
    // (formulas might reference other rows, so removing a row can affect calculations)
    // Use runInAction to separate cacheVersion increment from the row removal
    // This prevents triggering the save reaction unnecessarily
    this.clearAllCaches();
    runInAction(() => {
      this.recipe.groups.forEach((group) => {
        group.rows.forEach((row) => {
          // clearAllCaches already calls clearFormulaCache which clears the cache
          // But we need to increment cacheVersion to trigger MobX reactivity
          row.cacheVersion++;
        });
      });
    });

    log.debug(`[GroupStore] Removed row ${rowIndex} from group ${this.symbol}`);
  }

  /**
   * Clear the group: remove non-template rows, reset template rows to template defaults.
   * Keeps name, symbol, formulas as defined in each row; clears values and re-applies column defaultValues.
   */
  clearGroup(): void {
    // Remove rows not defined in template (iterate backwards to avoid index shifting)
    for (let i = this.rows.length - 1; i >= 0; i--) {
      if (!this.rows[i].definedInTemplate) {
        this.rows.splice(i, 1);
      }
    }
    // Reset template-defined rows: clear values, re-apply Constant columns (e.g. name), then column defaults
    this.rows.forEach((row) => {
      row.clearValues();
      this.columns.forEach((col) => {
        if (col.sourceType === Source.Constant && col.symbol === 'name') {
          row.setValue(col.symbol, row.name);
        } else if (col.defaultValue !== undefined && col.defaultValue !== null) {
          row.setValue(col.symbol, col.defaultValue);
        }
      });
    });

    this.clearAllCaches();
    runInAction(() => {
      this.recipe.groups.forEach((group) => {
        group.rows.forEach((row) => {
          row.cacheVersion++;
        });
      });
    });

    log.debug(`[GroupStore] Cleared group ${this.symbol}`);
  }

  /**
   * Clear formula cache in all rows in ALL groups.
   * Called when a value changes to invalidate dependent formulas across the entire recipe.
   * This handles cross-group dependencies (e.g., summary formulas that reference other groups).
   */
  clearAllCaches(): void {
    // Clear caches in ALL groups (for cross-group dependencies)
    this.recipe.groups.forEach((group) => {
      group.rows.forEach((row) => {
        row.clearFormulaCache();
      });
    });
  }

  /**
   * Update a row (for batch updates)
   */
  updateRow(rowIndex: number, _updates: Partial<RecipeGroupState>): void {
    const row = this.rows[rowIndex];
    if (!row) {
      log.warn(`[GroupStore] Row ${rowIndex} not found`);
      return;
    }

    // Apply updates - this is for any row-level properties
    // Individual cell updates go through row.setValue()
    log.debug(`[GroupStore] Updated row ${rowIndex} in group ${this.symbol}`);
  }

  /**
   * Export to plain object for Zustand sync
   */
  toState(): RecipeGroupState {
    return {
      id: this.id,
      symbol: this.symbol,
      name: this.name,
      color: this.color,
      rowType: this.rowType,
      scrolling: this.scrolling,
      allowSeveral: this.allowSeveral,
      copyLastRow: this.copyLastRow,
      comments: this.comments,
      regexFirstInclude: this.regexFirstInclude,
      regexFirstExclude: this.regexFirstExclude,
      filterColumn: this.filterColumn,
      canAddGroup: this.canAddGroup,
      canRemoveGroup: this.canRemoveGroup,
      commentColSpan: this.commentColSpan,
      showAllComments: this.showAllComments,
      columns: this.columns,
      rows: this.rows.map((row) => row.toState()),
      // Do not recompute here: calling recipe.toState() from inside group.toState() recurses (RecipeStore.toState
      // maps every group.toState()). Live UI uses isIfConditionColumnVisible + recipe snapshot in RecipeGroup instead.
      calculatedVisibility: this.calculatedVisibility,
    };
  }
}
